import React, {
  useEffect,
  useState,
  useContext,
  useMemo,
  useCallback,
} from 'react';
import { Form, Modal, message } from 'antd';
import { useTranslation } from 'react-i18next';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import { IGroupsData, IUser } from '@pages/Access/types';
import SessionContext from '@providers/Session/SessionContext';
import UserForm from './UserForm';

interface IUserViewModalProps {
  activeTabPane: string;
  isMotBusiness: boolean | undefined;
  userId: string;
  visible: boolean;
  onClose: () => void;
  mutateUsers: (type: 'CREATE' | 'UPDATE' | 'DELETE', data: any) => void;
}

const UserViewerModal: React.FC<IUserViewModalProps> = ({
  activeTabPane,
  isMotBusiness,
  userId,
  visible,
  onClose,
  mutateUsers,
}) => {
  const { keycloak } = useContext(KeycloakContext);
  const { userLogged } = useContext(SessionContext);
  const customerId = keycloak.idTokenParsed?.customerId;
  const isNewUser = userId === '';

  const { t } = useTranslation(['users']);
  const [form] = Form.useForm();

  const [saveLoading, setSaveLoading] = useState(false);
  const [customerIdFromGroup, setCustomerIdFromGroup] = useState<
    number | number[]
  >();

  const { data: userToUpdate } = useSwr<IUser>(
    !isNewUser ? `/users/${userId}` : null,
  );

  const isUserLoggedToUpdate = userId === userLogged?.sub;

  const { data: groupsData } = useSwr<IGroupsData>(`/groups/`, {
    params: {
      filter:
        activeTabPane === 'customers-users' && isMotBusiness
          ? 'CUSTOMERS'
          : 'MINE',
    },
  });

  useEffect(() => {
    !isNewUser && setCustomerIdFromGroup(userToUpdate?.attributes.customerId);
  }, [isNewUser, userToUpdate?.attributes.customerId, customerId]);

  const formInitialValues = useMemo(() => {
    if (!isNewUser && userToUpdate && groupsData) {
      const fields: any = { ...userToUpdate };
      if (!fields.attributes.customerIds) {
        fields.attributes.customerIds = [fields.attributes.customerId];
      }
      fields.groupId = fields.groups?.[0]?.id;
      return fields;
    }
    return {
      attributes: {
        customerIds: [customerIdFromGroup || customerId],
      },
    };
  }, [customerIdFromGroup, customerId, groupsData, isNewUser, userToUpdate]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue(formInitialValues);
    setCustomerIdFromGroup(
      form.getFieldValue(['attributes', 'customerIds'])[0],
    );
  }, [form, visible, formInitialValues]);

  const close = useCallback(() => {
    setCustomerIdFromGroup(customerId);
    onClose();
  }, [onClose, customerId]);

  const optionsGroups = useMemo(
    () =>
      groupsData?.content
        ?.map(group =>
          activeTabPane === 'customers-users' && isMotBusiness
            ? group.subGroups?.map(subGroup => ({
                label: `${subGroup.attributes.prettyName} - ${subGroup.attributes.customerName}`,
                value: subGroup.id,
                customerId: subGroup.attributes.customerId,
              }))
            : {
                label: group.attributes.prettyName,
                value: group.id,
                customerId: group.attributes.customerId,
              },
        )
        ?.flat(),
    [activeTabPane, groupsData?.content, isMotBusiness],
  );

  const onFinish = useCallback(
    async (values: any) => {
      setSaveLoading(true);
      const { groupId, ...newUser } = values;

      try {
        let userIdToMutate = '';
        if (isNewUser) {
          // CREATION
          const { data } = await api.post('/users/', newUser);
          userIdToMutate = data.id;
        } else {
          // UPDATE
          await api.put(`/users/${userId}`, newUser);
          userIdToMutate = userId;
        }

        await api.put(`/users/${userIdToMutate}/groups/`, [{ id: groupId }]);

        const { data: user } = await api.get(`/users/${userIdToMutate}`);
        mutateUsers(isNewUser ? 'CREATE' : 'UPDATE', user);
        message.success(
          t(`msgs.success-${isNewUser ? 'creating' : 'updating'}-user`),
        );
        close();
      } catch (err) {
        const { response }: any = err;
        if (response.status === 409) {
          if (response?.data?.message?.includes('username')) {
            form.setFields([
              {
                name: 'username',
                errors: [t('modal.form.msgs.userNameAlreadyRegistered')],
              },
            ]);
          }
          if (response?.data?.message?.includes('email')) {
            form.setFields([
              {
                name: 'email',
                errors: [t('modal.form.msgs.emailAlreadyRegistered')],
              },
            ]);
          }
        } else {
          message.error(
            t(
              `msgs.${
                isNewUser ? 'error-creating-user' : 'error-updating-user'
              }`,
            ),
          );
        }
      }

      setSaveLoading(false);
    },
    [isNewUser, form, t, userId, mutateUsers, close],
  );

  return (
    <Modal
      cancelText={t('modal.buttons.cancelButton')}
      confirmLoading={saveLoading}
      maskClosable={false}
      okButtonProps={{ form: 'userForm', htmlType: 'submit' }}
      okText={t('modal.buttons.saveButton')}
      afterClose={() => document.body.style.removeProperty('overflow')}
      title={
        isNewUser ? t('modal.titles.newUser') : t('modal.titles.updateUser')
      }
      open={visible}
      width="100%"
      style={{ maxWidth: 720 }}
      bodyStyle={{ padding: '32px 48px' }}
      centered
      onCancel={close}
    >
      <UserForm
        allowRoles
        form={form}
        isNewUser={isNewUser}
        userToUpdateId={userToUpdate?.attributes?.customerId}
        isUserLoggedToUpdate={isUserLoggedToUpdate}
        loading={!isNewUser && !userToUpdate}
        optionsGroups={optionsGroups}
        onFormFinish={onFinish}
      />
    </Modal>
  );
};

export default UserViewerModal;
